import { useEffect, useState } from "react";
import { toast } from "sonner";
import GlassSheet from "@/components/v2/GlassSheet";
import { api } from "@/hooks/useAuth";

// Minimal flat SVG offerings — soft cosmic palette.
const PRASAD = [
  { kind: "makhan", label: "Makhan", color: "#FFFDF6", accent: "#FFE9B4" },
  { kind: "mishri", label: "Mishri", color: "#F8FAFF", accent: "#C7D2FE" },
  { kind: "coconut", label: "Coconut", color: "#E6D8B6", accent: "#8C6B29" },
  { kind: "banana", label: "Banana", color: "#F3E4A9", accent: "#C7A24B" },
  { kind: "mango", label: "Mango", color: "#F3C27C", accent: "#D97706" },
  { kind: "honey", label: "Honey", color: "#F0D18A", accent: "#8C6B29" },
  { kind: "kheer", label: "Kheer", color: "#F3EFE4", accent: "#DFB96E" },
  { kind: "laddoo", label: "Laddoo", color: "#F0B869", accent: "#8C6B29" },
  { kind: "tulsi", label: "Tulsi", color: "#B6DBB2", accent: "#5BAD7E" },
];

function PrasadIcon({ color, accent }) {
  return (
    <svg viewBox="0 0 48 48" className="w-9 h-9">
      <defs>
        <radialGradient id={`g-${color}`} cx="35%" cy="30%">
          <stop offset="0%" stopColor="#fff" stopOpacity="0.9" />
          <stop offset="60%" stopColor={color} />
          <stop offset="100%" stopColor={accent} />
        </radialGradient>
      </defs>
      <circle cx="24" cy="26" r="14" fill={`url(#g-${color})`} />
      <path d="M18 14 Q24 8 30 14" stroke={accent} strokeWidth="1.5" fill="none" strokeLinecap="round" />
      <circle cx="20" cy="22" r="1.5" fill="#fff" opacity="0.65" />
    </svg>
  );
}

export default function PrasadSheet({ open, onClose, user }) {
  const [chosen, setChosen] = useState(null);
  const [gratitude, setGratitude] = useState("");
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    if (!open || !user) return;
    (async () => {
      try { const { data } = await api.get("/prasad"); setHistory(data.items || []); } catch (e) {}
    })();
  }, [open, user]);

  const submit = async () => {
    if (!chosen) return;
    setSending(true);
    try {
      await api.post("/prasad", { kind: chosen, gratitude });
      toast.success("🙏 I receive your gratitude.");
      setChosen(null); setGratitude("");
      const { data } = await api.get("/prasad"); setHistory(data.items || []);
    } catch (e) { toast.error("Please sign in to offer Prasad."); }
    finally { setSending(false); }
  };

  return (
    <GlassSheet open={open} onClose={onClose} eyebrow="Offer Prasad" title="What would you like to offer today?" testid="prasad-sheet">
      <div className="grid grid-cols-3 gap-3 mb-5">
        {PRASAD.map((p) => (
          <button
            key={p.kind}
            data-testid={`prasad-${p.kind}`}
            onClick={() => setChosen(p.kind)}
            className={"flex flex-col items-center gap-2 py-3 rounded-2xl border transition-all " +
              (chosen === p.kind ? "border-amber-300/60 bg-amber-100/5" : "border-white/10 bg-white/5 hover:border-white/25")}
          >
            <PrasadIcon color={p.color} accent={p.accent} />
            <span className="text-[11px] tracking-wide text-slate-200">{p.label}</span>
          </button>
        ))}
      </div>

      {chosen && (
        <div className="space-y-3">
          <label className="block">
            <span className="block text-[11px] uppercase tracking-wider text-slate-400 mb-1">What are you grateful for? (optional)</span>
            <textarea
              value={gratitude}
              onChange={(e) => setGratitude(e.target.value)}
              rows={3}
              data-testid="prasad-gratitude"
              className="w-full rounded-2xl bg-white/5 border border-white/10 px-4 py-3 text-slate-100 focus:border-amber-300/50 focus:outline-none"
              placeholder="Tell KALKI…"
            />
          </label>
          <button
            onClick={submit} disabled={sending} data-testid="prasad-offer"
            className="w-full rounded-full py-3 text-[14px] font-medium text-slate-50 disabled:opacity-40"
            style={{ background: "linear-gradient(140deg,#3B82F6,#7C3AED)", border: "1px solid rgba(223,185,110,0.5)" }}
          >
            {sending ? "Offering…" : "Offer Prasad"}
          </button>
        </div>
      )}

      {history.length > 0 && (
        <div className="mt-6">
          <div className="text-[10px] uppercase tracking-[0.3em] text-slate-400 mb-2">Recent offerings</div>
          <div className="flex flex-wrap gap-2">
            {history.slice(0, 10).map((h) => (
              <span key={h.id} className="text-[11px] text-slate-300 rounded-full border border-white/10 bg-white/5 px-3 py-1">
                {h.kind}
              </span>
            ))}
          </div>
        </div>
      )}
    </GlassSheet>
  );
}
